import React, { useRef, useState } from "react";
import { Edit } from "lucide-react";
import logoAtletain from "../../utils/Logo_Atletain.png";
import { ATHLETE_ATTRIBUTES_DEFAULTS } from "../../models";
import { formatHeight } from "../../utils/formatHeight";
import { useUploadAthleteAvatar } from "../../hooks/queries/useAthletes";

const UserProfile = ({ data }) => {
  const fileInputRef = useRef(null);
  const [isSavingPhoto, setIsSavingPhoto] = useState(false);
  const [photoMessage, setPhotoMessage] = useState("");
  const uploadAvatar = useUploadAthleteAvatar();

  if (!data) return <div className="p-6">Cargando perfil...</div>;

  const rawAttrs = data?.attributes || {};
  const attributes = { ...ATHLETE_ATTRIBUTES_DEFAULTS, ...rawAttrs };
  const hasAvatar = Boolean(attributes.avatar_url);
  const avatarUrl = attributes.avatar_url || logoAtletain;

  const profileStats = [
    { label: "Peso", value: `${attributes.weight} kg` },
    { label: "Altura", value: formatHeight(attributes.height) },
    { label: "Edad", value: `${attributes.age} años` },
  ];

  const handlePhotoChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setPhotoMessage("Selecciona una imagen valida.");
      return;
    }

    setIsSavingPhoto(true);
    setPhotoMessage("");

    try {
      // The upload invalidates the session, so the new photo arrives with the
      // refetched profile.
      await uploadAvatar.mutateAsync({ id: data.id, file });
      setPhotoMessage("Foto actualizada.");
      // Only the confirmation fades; an error stays until the next attempt.
      setTimeout(() => setPhotoMessage(""), 3500);
    } catch (error) {
      setPhotoMessage(error?.message || "No se pudo actualizar la foto.");
    } finally {
      setIsSavingPhoto(false);
      event.target.value = "";
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
        {/* Profile picture */}
        <div className="flex-shrink-0">
          <div className="relative w-32 h-40">
            <div
              className={`h-full w-full overflow-hidden rounded-lg ${
                hasAvatar
                  ? ""
                  : "flex items-center justify-center bg-blue-950 p-5"
              }`}
            >
              <img
                src={avatarUrl}
                alt={data.full_name}
                className={
                  hasAvatar
                    ? "w-full h-full object-cover"
                    : "w-full object-contain"
                }
              />
            </div>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isSavingPhoto}
              aria-label="Cambiar foto"
              title="Cambiar foto"
              data-analytics-id="profile-change-photo"
              className="absolute right-0 top-0 flex h-8 w-8 translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-blue-900 text-white shadow-lg ring-2 ring-white transition-colors hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Edit size={16} />
            </button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handlePhotoChange}
            className="hidden"
          />
          {photoMessage && (
            <p className="mt-2 text-center text-xs font-medium text-gray-600">
              {photoMessage}
            </p>
          )}
        </div>

        {/* Profile info */}
        <div className="w-full flex-1 text-center sm:text-left">
          <h3 className="font-semibold text-xl text-gray-900 mb-1">
            {data.full_name}
          </h3>

          <p className="text-sm text-gray-600 mb-1">{data.category}</p>

          <p className="text-lg font-bold text-gray-900 mb-2">
            {attributes?.points?.toLocaleString()} pts
          </p>

          {/* Sport and Position */}
          <div className="mb-4">
            <p className="text-sm text-gray-700">
              <span className="font-medium">Deporte: </span>
              {attributes?.sport}
            </p>
            <p className="text-sm text-gray-700">
              <span className="font-medium">Posicion: </span>
              {data.position}
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            {profileStats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-xs text-gray-500 mb-1">{stat.label}</p>
                <p className="font-semibold text-gray-900">{stat.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
